import { useEffect, useState } from "react";
import Button from "@shared/v2/Button";
import { XmarkSolidV6 } from "@shared/v2/Icomoon";
import IconButton from "@shared/v2/IconButton";
import Typography from "@shared/v2/Typography";
import Backdrop from "./Backdrop";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import ProcessingPurchase from "./ProcessingPurchase";
import PurchaseCompleteModal from "./PurchaseCompleteModal";
import useProducts from "./hooks/useProducts";
import PurchaseBarContainer from "./PurchaseBarContainer";
import { Product } from "../Support/Account/AddOnsSupport/types";

const getTitle = (step: number) => {
  if (step === 2) return "Confirm Plan";
  if (step === 3) return "Terms of Use";
  return "Select a Plan";
};

interface PurchaseBarProps {
  completeModalProps: {
    Icon: () => JSX.Element;
    title: string;
    description: string;
    url: string;
  };
  getSubtitle?: (product?: Product) => string;
  selectProduct?: (products: Product[]) => Product | undefined;
  productFamilySlug: string;
  step1Children?: React.ReactNode;
  terms?: React.ReactNode;
  title: string;
}

const PurchaseBar: React.FC<PurchaseBarProps> = ({
  completeModalProps,
  getSubtitle,
  selectProduct,
  productFamilySlug,
  step1Children,
  terms,
  title,
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<null | Product>(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const products = useProducts(productFamilySlug);
  const autoProduct = selectProduct?.(products);

  useEffect(() => {
    if (autoProduct) {
      setSelectedProduct(autoProduct);
      setStep(2);
    }
  }, [autoProduct?.id]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const iframe = document.getElementById("purchase-iframe") as HTMLIFrameElement | null;
    if (iframe) {
      iframe.addEventListener("load", () => {
        setIframeLoaded(true);
      });
    }
    if (queryParams.has("signup")) setOpen(true);
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes("brivity.com")) return;
      const plan = products.find((p) => String(p.details.productId) === event.data);
      if (plan) {
        setOpen(true);
        setSelectedProduct(plan);
        setStep(2);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [iframeLoaded]);

  return (
    <PurchaseBarContainer
      className={step > 4 ? "tw-invisible" : ""}
      style={{ maxWidth: products.length ? products.length * 300 : 900 }}
    >
      {!open && (
        <>
          <div className="tw-flex tw-flex-col tw-gap-[4px]">
            {/* @ts-expect-error props */}
            <Typography
              variant="subtitle-m"
              className="tw-transition-colors group-hover:!tw-text-theme-primary"
            >
              {title}
            </Typography>
            {/* @ts-expect-error props */}
            <Typography variant="body-s" className="!tw-text-gray-50">
              {getSubtitle
                ? getSubtitle(autoProduct || products[0])
                : `Starting at only $${products[0]?.details.unitPrice} a month. Cancel anytime.`}
            </Typography>
          </div>
          {/* @ts-expect-error props */}
          <Button
            size="medium"
            className="tw-whitespace-nowrap"
            onClick={() => {
              if (autoProduct) {
                setSelectedProduct(autoProduct);
                setStep(2);
                setOpen(true);
              } else {
                setStep(1);
                setOpen(true);
              }
            }}
          >
            Sign Up
          </Button>
        </>
      )}
      {open && (
        <>
          <div className="tw-w-full tw-flex tw-flex-col tw-gap-[24px]">
            {step < 4 && (
              <div className="tw-flex tw-justify-between tw-gap-[4px]">
                <div className="tw-flex tw-gap-[8px] tw-items-baseline">
                  {/* @ts-expect-error props */}
                  <Typography variant="headline3">{getTitle(step)}</Typography>
                  {/* @ts-expect-error props */}
                  <Typography variant="body-m" className="!tw-text-gray-50">
                    Step {autoProduct ? step - 1 : step} of {autoProduct ? 2 : 3}
                  </Typography>
                </div>
                {/* @ts-expect-error props */}
                <IconButton size="small" schema="tertiary" onClick={() => setOpen(false)}>
                  <XmarkSolidV6 />
                </IconButton>
              </div>
            )}

            {step === 1 && (
              <Step1
                recommendedProductId={products[1]?.details.productId}
                children={step1Children}
                products={products}
                onSelectPlan={(plan) => {
                  setSelectedProduct(plan);
                  setStep(2);
                }}
              />
            )}

            {step === 2 && (
              <Step2
                onBack={autoProduct ? null : () => setStep(1)}
                onNext={() => setStep(3)}
                product={selectedProduct}
              />
            )}

            {step === 3 && (
              <Step3
                children={terms}
                onBack={() => setStep(2)}
                onAccept={() => setStep(4)}
                product={selectedProduct}
              />
            )}

            {step === 4 && <ProcessingPurchase product={selectedProduct} onSuccess={() => setStep(5)} />}

            <PurchaseCompleteModal open={step === 5} onCancel={() => setStep(6)} {...completeModalProps} />
          </div>
        </>
      )}
      <Backdrop open={open && step < 6} />
    </PurchaseBarContainer>
  );
};

export default PurchaseBar;
